import styles from './CompanyRole.module.scss';
import classNames from 'classnames/bind';
import { Box, Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { useTranslation } from 'react-i18next';
import { BaseHeaderTitle, BaseRow } from '~/components/common';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { useCallback, useEffect, useState } from 'react';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import CompanyRoleList from '~/components/company-roles/company-role-list/CompanyRoleList';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetCompanyRole, handleGetCompanyRoles } from '~/thunks/company-role/companyRoleThunk';
import CompanyRoleEdit from '~/components/company-roles/company-role-edit/CompanyRoleEdit';
import { companyRoleActions } from '~/thunks/company-role/companyRoleSlice';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IPayloadDeleteCompanyRole } from '~/utils/interface/company-role';
import companyRoleApi from '~/apis/company-role';

const cx = classNames.bind(styles);

const Role = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/company-role');
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.companyRole
  );
  //#endregion Selector

  //#region Declare State
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [editData, setEditData] = useState<any>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetCompanyRoles(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setEditData(undefined);
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(open);
    if (needRefreshData) dispatch(companyRoleActions.changeNeedRefreshData(true));
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetCompanyRole(id)).unwrap();
      setEditData(response);
      setEditDialogMode(DIALOG_MODE_EDIT);
      setOpenEditDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(companyRoleActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(companyRoleActions.changeMenuSelectId(undefined));
    dispatch(companyRoleActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelected = useCallback(async () => {
    let payloadDeleteAccounts: IPayloadDeleteCompanyRole =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await companyRoleApi.deleteCompanyRole(payloadDeleteAccounts);
      notify('success', t('deleteSuccessMessage'));
      dispatch(companyRoleActions.changeNeedRefreshData(true));
      dispatch(companyRoleActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);
  //#endregion Handle Function

  return (
    <Box className='flex flex-col'>
      <Box className='flex flex-col mb-[20px]'>
        <BaseHeaderTitle title={t('title')} />
      </Box>
      <BaseRow className='gap-[10px]'>
        <Button
          className={cx(
            { 'visibility: visible': selectedIds.length > 0 },
            { 'visibility: hidden': selectedIds.length <= 0 }
          )}
          onClick={openAlertDeleteModal}
          variant='contained'
          color='error'
          disableElevation
          startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
        >
          {t('delete', { ns: 'admin/common' })}
        </Button>
        <Box className='ml-auto mb-[20px]'>
          {!openEditDialog && permissions[PERMISSIONS.companyRole_create] && (
            <Button
              variant='contained'
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={() => handleSetOpen(true)}
            >
              {t('addRole')}
            </Button>
          )}
        </Box>
      </BaseRow>

      <BaseRow className='gap-[10px]'>
        <Box className='w-full'>
          <CompanyRoleList onOpenEdit={handleOpenEdit} editData={editData} />
        </Box>
        {openEditDialog && (
          <Box className='lg:flex-[0_0_30%]'>
            <CompanyRoleEdit setOpen={handleSetOpen} mode={editDialogMode} editData={editData} />
          </Box>
        )}
      </BaseRow>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
    </Box>
  );
};

export default Role;
